'use client';

import React from 'react';
import { Menu, PenSquare, ChevronLeft, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface Props {
  onMenuClick: () => void;
  onNewChat: () => void;
  isGuest?: boolean;
}

export default function ChatHeader({ onMenuClick, onNewChat, isGuest = false }: Props) {
  const router = useRouter();

  return (
    <div className="flex items-center gap-3 px-4 pt-3 pb-3 bg-background/95 backdrop-blur-md border-b border-border/60 sticky top-0 z-10">
      {/* Back — mobile (authenticated) */}
      {!isGuest && (
        <button
          onClick={() => router.push('/home-screen')}
          className="md:hidden w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors active:scale-95"
        >
          <ChevronLeft size={20} className="text-muted-foreground" />
        </button>
      )}

      {/* Back to home — guest */}
      {isGuest && (
        <button
          onClick={() => router.push('/')}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors active:scale-95"
        >
          <ChevronLeft size={20} className="text-muted-foreground" />
        </button>
      )}

      {/* History — desktop (authenticated) */}
      {!isGuest && (
        <button
          onClick={onMenuClick}
          className="hidden md:flex w-9 h-9 rounded-full items-center justify-center hover:bg-muted transition-colors"
        >
          <Menu size={18} className="text-muted-foreground" />
        </button>
      )}

      {/* Avatar + name */}
      <div className="flex items-center gap-2.5 flex-1">
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full lavender-gradient flex items-center justify-center shadow-sm">
            <span className="text-white text-sm font-extrabold tracking-tight">C</span>
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />
        </div>
        <div>
          <p className="text-[15px] font-bold text-foreground leading-none tracking-tight">Clingy</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
            Here for you
          </p>
        </div>
      </div>

      {/* Guest: sign up CTA */}
      {isGuest && (
        <Link
          href="/onboarding-sign-up-login-screen"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary/10 text-primary text-xs font-bold transition-all hover:bg-primary/20 active:scale-95"
        >
          Sign up
          <ArrowRight size={12} />
        </Link>
      )}

      {/* Authenticated: new chat */}
      {!isGuest && (
        <button
          onClick={onNewChat}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors active:scale-95"
          title="New conversation"
        >
          <PenSquare size={16} className="text-muted-foreground" />
        </button>
      )}
    </div>
  );
}